import debug from 'debug';
import { nanoid } from 'nanoid';
import { useCallback, useContext, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { merge } from 'ts-deepmerge';
import { SocketContext } from '@/contexts/socket';
import { useController } from '@/hooks/useController';
import { initialUserConfig, integrationIdSelector, userConfigState, widgetIdSelector, } from '@/state/config';
import { chatEventsState, isSessionExpiredSelector, sessionIdSelector, userSelector, } from '@/state/session';
import { SocketEvent, } from '@/ts/socket';
import { getTimestamp, normalizeContext } from '@/util/utils';
import { useSessionId } from './useSessionId';
const getAsyncLocation = () => new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition((data) => resolve(data.coords), reject);
});
export const useSocket = (socketProvider) => {
    var _a;
    // If the hook is not wrapped into the context provider, we pass it as argument
    const socket = (_a = useContext(SocketContext)) !== null && _a !== void 0 ? _a : socketProvider;
    const integrationId = useRecoilValue(integrationIdSelector);
    const setSessionId = useSessionId(integrationId);
    const initialUserConfigValue = useRecoilValue(initialUserConfig);
    const isSessionExpired = useRecoilValue(isSessionExpiredSelector);
    const session_id = useRecoilValue(sessionIdSelector);
    const user = useRecoilValue(userSelector);
    const widgetId = useRecoilValue(widgetIdSelector);
    const setChatEvents = useSetRecoilState(chatEventsState);
    const { notifyUserActivity } = useController();
    const [userConfig, setUserConfig] = useRecoilState(userConfigState);
    const log = useMemo(() => debug(`moveo:iframe:${widgetId}:socket-client`), [widgetId]);
    const sendMessage = useCallback(({ text, label, attachments, }) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "sendMessage()", socket is not connected or session_id is not set');
            return;
        }
        socket.emit(SocketEvent.message_send, {
            input: {
                text,
                attachments,
            },
            session_id,
            channel: 'web',
            timestamp: getTimestamp(),
        });
        if (user) {
            // The context is masked for security reasons but some user fields
            // such as display_name, avatar and user_id are not masked
            const chatEvent = {
                event_id: nanoid(),
                event_type: 'user_message',
                author_name: user.display_name,
                author_avatar: user.avatar,
                author_id: user.user_id,
                created_at: getTimestamp(),
                text: label || text,
                attachments,
            };
            setChatEvents((currentState) => [...currentState, chatEvent]);
        }
    }, [log, socket, session_id, user, setChatEvents]);
    const requestUploadPresignedUrl = useCallback((params) => {
        const { fileId, originalName, fileType } = params;
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "requestUploadPresignedUrl()", socket is not connected or session_id is not set');
            return;
        }
        socket.emit(SocketEvent.session_files_upload_request, {
            file_id: fileId,
            session_id,
            original_name: originalName,
            file_type: fileType,
        });
    }, [log, session_id, socket]);
    const sendCompose = useCallback((action) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "sendCompose()", socket is not connected or session_id is not set');
            return;
        }
        socket.volatile.emit(SocketEvent.message_compose, {
            session_id,
            action,
            timestamp: getTimestamp(),
        });
    }, [log, socket, session_id]);
    /**
     * Sends a close event.
     */
    const closeSession = useCallback(() => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "closeSession()", socket is not connected or session_id is not set');
            return;
        }
        // If the session is expired, we don't send the close event to avoid looping
        // the session_expired event
        if (!isSessionExpired) {
            socket.emit(SocketEvent.session_close, {
                session_id,
                timestamp: getTimestamp(),
            });
        }
        setSessionId(null);
        setUserConfig((prev) => merge({}, prev, initialUserConfigValue));
    }, [
        log,
        session_id,
        setUserConfig,
        initialUserConfigValue,
        socket,
        isSessionExpired,
        setSessionId,
    ]);
    const sendRating = useCallback(({ rating, feedback }) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "sendRating()", socket is not connected or session_id is not set');
            return;
        }
        socket.emit(SocketEvent.session_rating, {
            session_id,
            timestamp: getTimestamp(),
            rating,
            feedback: feedback || undefined,
        });
    }, [socket, session_id, log]);
    const reopenConversation = useCallback(() => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "reopenConversation()", socket is not connected or session_id is not set');
            return;
        }
        socket.emit(SocketEvent.conversation_reopen, {
            session_id,
        });
    }, [log, socket, session_id]);
    const sendVisitorActivity = useCallback((timestamp) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id || isSessionExpired) {
            log('Ignore "sendVisitorActivity()", socket is not connected or session_id is not set or session is expired');
            return;
        }
        // Defer to next tick to avoid state update during render
        setTimeout(notifyUserActivity, 0);
        socket.volatile.emit(SocketEvent.session_activity, {
            session_id,
            timestamp,
        });
        log('Sent visitor activity for session: %s', session_id);
    }, [socket, session_id, isSessionExpired, notifyUserActivity, log]);
    const markMessagesAsRead = useCallback(() => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "markMessagesAsRead()", socket is not connected or session_id is not set');
            return;
        }
        socket.volatile.emit(SocketEvent.message_read, {
            session_id,
            timestamp: getTimestamp(),
        });
        setChatEvents((currentState) => currentState.map((e) => {
            if (e.event_type === 'agent_message' ||
                e.event_type === 'brain_message') {
                return Object.assign(Object.assign({}, e), { read_at: e.read_at || getTimestamp() });
            }
            return e;
        }));
    }, [log, session_id, socket, setChatEvents]);
    const sendMessageFeedback = useCallback((request_id, value) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "sendMessageFeedback()", socket is not connected or session_id is not set');
            return;
        }
        socket.volatile.emit(SocketEvent.message_rating, {
            session_id,
            timestamp: getTimestamp(),
            request_id,
            value,
        });
    }, [log, session_id, socket]);
    const updateContext = useCallback((context) => {
        if (!(socket === null || socket === void 0 ? void 0 : socket.connected) || !session_id) {
            log('Ignore "updateContext()", socket is not connected or session_id is not set');
            return;
        }
        const updatedContext = normalizeContext(context);
        // Defer to next tick to avoid state update during render
        setTimeout(() => {
            socket.emit(SocketEvent.context_update, {
                session_id,
                context: updatedContext,
                timestamp: getTimestamp(),
            });
        }, 0);
    }, [log, session_id, socket]);
    const updateLocation = useCallback(async () => {
        var _a;
        if (!((_a = userConfig.visitor_information) === null || _a === void 0 ? void 0 : _a.location)) {
            return;
        }
        try {
            const location = await getAsyncLocation();
            const newContext = {
                user: {
                    location: {
                        latitude: location.latitude,
                        longitude: location.longitude,
                    },
                },
            };
            updateContext(newContext);
        }
        catch (_b) {
            log('Location not available or the user rejected it');
            return;
        }
    }, [updateContext, userConfig, log]);
    return {
        sendMessageFeedback,
        closeSession,
        markMessagesAsRead,
        reopenConversation,
        sendCompose,
        sendMessage,
        sendRating,
        sendVisitorActivity,
        updateContext,
        requestUploadPresignedUrl,
        updateLocation,
    };
};
